import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Grid,
  Heading,
  Text,
  Card,
  Avatar,
  Badge,
  Skeleton,
  Animate,
  Banner,
  Button,
  useIsDesktop
} from 'wss3-forge'
import {
  ArrowRight20Regular,
  Calendar20Regular,
  Person20Regular,
  Shield20Regular,
  Receipt20Regular,
  History20Regular
} from '@fluentui/react-icons'

interface UserProfile {
  first_name?: string
  last_name?: string
  display_name?: string
  avatar_url?: string
  is_webba_member?: boolean
}

const WEBBA_SERVICES = [
  {
    id: 'forge',
    name: 'Forge',
    description: 'Design system and UI components',
    url: 'https://forge.webba-creative.com',
    isNew: true
  },
  {
    id: 'markdoc',
    name: 'Markdoc',
    description: 'Markdown documentation engine',
    url: 'https://markdoc.webba-creative.com'
  },
  {
    id: 'uwhy',
    name: 'uWhy',
    description: 'User research and feedback',
    url: 'https://uwhy.com'
  }
]

export function Home() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  usePageTitle('Home')
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      loadProfile()
    }
  }, [user])

  const loadProfile = async () => {
    if (!user) return

    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, display_name, avatar_url, is_webba_member')
        .eq('user_id', user.id)
        .single()

      setProfile(data || {})
    } catch (err) {
      console.error('Error loading profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const displayName = profile?.display_name || profile?.first_name || user?.email?.split('@')[0] || 'User'

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <HStack gap="md" style={{ alignItems: 'center' }}>
            {loading ? (
              <>
                <Skeleton variant="circular" width={48} height={48} />
                <VStack gap="xs">
                  <Skeleton width={200} height={28} />
                  <Skeleton width={260} height={16} />
                </VStack>
              </>
            ) : (
              <>
                <Avatar name={displayName} src={profile?.avatar_url} size="lg" />
                <VStack gap="none">
                  <Heading level={2}>{getGreeting()}, {displayName}</Heading>
                  <Text color="muted">Welcome to your Webba ID dashboard</Text>
                </VStack>
              </>
            )}
          </HStack>
        </Animate>

        <Animate type="fadeIn" delay={25}>
          <VStack gap="sm">
            <Banner
              variant="brand"
              title="Introducing Forge (WSS3)"
              action={isDesktop ? {
                label: 'Discover Forge',
                onClick: () => window.open('https://forge.webba-creative.com', '_blank')
              } : undefined}
            >
              Our new design system is here! Build beautiful, consistent interfaces with Forge.
            </Banner>
            {!isDesktop && (
              <Button onClick={() => window.open('https://forge.webba-creative.com', '_blank')} style={{ width: '100%' }}>
                Discover Forge
              </Button>
            )}
          </VStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          <VStack gap="sm">
            <Heading level={5}>Webba Services</Heading>
            <Grid columns={{ xs: 1, sm: 3 }} gap="sm">
              {WEBBA_SERVICES.map((service) => (
                <Card
                  key={service.id}
                  padding="md"
                  hoverable
                  onClick={() => window.open(service.url, '_blank')}
                >
                  <VStack gap="sm">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Text weight="medium">{service.name}</Text>
                      {service.isNew && <Badge variant="primary" size="sm">New</Badge>}
                      <div style={{ flex: 1 }} />
                      <ArrowRight20Regular style={{ color: 'var(--text-muted)' }} />
                    </HStack>
                    <Text size="sm" color="muted">{service.description}</Text>
                  </VStack>
                </Card>
              ))}
            </Grid>
          </VStack>
        </Animate>

        <Animate type="slideInUp" delay={100}>
          <VStack gap="sm">
            <Heading level={5}>Quick Access</Heading>
            <Grid columns={{ xs: 2, md: 4 }} gap="sm">
              <Card padding="md" hoverable onClick={() => navigate('/account')}>
                <VStack gap="sm">
                  <Person20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Text weight="medium">Profile</Text>
                  <Text size="sm" color="muted">Edit your info</Text>
                </VStack>
              </Card>

              <Card padding="md" hoverable onClick={() => navigate('/privacy')}>
                <VStack gap="sm">
                  <Shield20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Text weight="medium">Privacy</Text>
                  <Text size="sm" color="muted">Manage apps</Text>
                </VStack>
              </Card>

              <Card padding="md" hoverable onClick={() => navigate('/subscriptions')}>
                <VStack gap="sm">
                  <Receipt20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Text weight="medium">Subscriptions</Text>
                  <Text size="sm" color="muted">Your plans</Text>
                </VStack>
              </Card>

              <Card padding="md" hoverable onClick={() => navigate('/transactions')}>
                <VStack gap="sm">
                  <History20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Text weight="medium">Transactions</Text>
                  <Text size="sm" color="muted">Payment history</Text>
                </VStack>
              </Card>
            </Grid>
          </VStack>
        </Animate>

        <Animate type="slideInUp" delay={150}>
          <Card padding="md">
            <VStack gap="sm">
              <Heading level={5} style={{ marginBottom: '0.5rem' }}>Account Overview</Heading>
              <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
                <VStack gap="xs">
                  <Text size="sm" color="muted">Email</Text>
                  <HStack gap="sm" style={{ alignItems: 'center' }}>
                    <Text size="sm">{user?.email}</Text>
                    {user?.email_confirmed_at && (
                      <Badge variant="success" size="sm">Verified</Badge>
                    )}
                  </HStack>
                </VStack>

                <VStack gap="xs">
                  <Text size="sm" color="muted">Full name</Text>
                  <Text size="sm">
                    {profile?.first_name || profile?.last_name
                      ? `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
                      : 'Not set'}
                  </Text>
                </VStack>

                <VStack gap="xs">
                  <Text size="sm" color="muted">Member since</Text>
                  <HStack gap="sm" style={{ alignItems: 'center' }}>
                    <Calendar20Regular style={{ color: 'var(--text-muted)', fontSize: '16px' }} />
                    <Text size="sm">
                      {user?.created_at
                        ? new Date(user.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : '-'}
                    </Text>
                  </HStack>
                </VStack>

                <VStack gap="xs">
                  <Text size="sm" color="muted">Last sign in</Text>
                  <Text size="sm">
                    {user?.last_sign_in_at
                      ? new Date(user.last_sign_in_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : '-'}
                  </Text>
                </VStack>
              </Grid>
            </VStack>
          </Card>
        </Animate>
      </VStack>
    </div>
  )
}
